import { useCallback, useEffect, useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { devLog } from "@/lib/log";
import { toast } from "sonner";
import { Users, FileDown, Loader2, AlertTriangle, CalendarClock, Share2, MessageCircle, Mail } from "lucide-react";
import TaxDisclaimer from "@/components/tax/TaxDisclaimer";
import { useShareToAccountant } from "@/hooks/useShareToAccountant";
import BiSelect from "@/components/BiSelect";
import FlagsPreflightCard from "@/components/tax/FlagsPreflightCard";
import PdfPreviewSheet from "@/components/tax/PdfPreviewSheet";

function money(n) {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * TPAR Draft — Taxable Payments Annual Report worksheet for building & construction.
 * Aggregates subcontractor payments for a given FY and offers a one-tap PDF download.
 */
export default function TparDraftCard() {
  const { accountantName, accountantEmail, bizName, abn, shareVia: shareToAccountant, prefetchPdf } = useShareToAccountant();
  const [data, setData] = useState(null);
  const [fy, setFy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewCfg, setPreviewCfg] = useState(null);

  const load = useCallback(async (year) => {
    setBusy(true);
    try {
      const params = year ? { fy: year } : {};
      const { data: res } = await api.get("/tax/tpar-draft", { params });
      setData(res);
      if (fy === null) setFy(res.fy);
    } catch (err) {
      devLog.warn("tpar-draft failed", err);
      toast.error("Couldn't load TPAR draft");
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (fy !== null && data && fy !== data.fy) load(fy);
  }, [fy, data, load]);

  // Warm the TPAR PDF cache so tapping Share fires the native OS share
  // sheet synchronously (iOS Safari requirement).
  useEffect(() => {
    if (!data) return;
    prefetchPdf(`/export/tpar-pdf?fy=${data.fy}&with_evidence=1`, `buildintel-tpar-${data.fy_label}.pdf`);
  }, [data, prefetchPdf]);

  const options = data ? [data.fy - 1, data.fy, data.fy + 1] : [];

  // First tap on Prepare TPAR opens the in-app preview — no first-tap
  // OS share, no same-tab PDF trap. Share happens from inside preview.
  const download = () => {
    if (!data) return;
    const text = buildSummary();
    const subject = bizName
      ? `${bizName} — TPAR ${data.fy_label}`
      : `TPAR ${data.fy_label} — ${data.subcontractor_count ?? 0} subcontractors`;
    setPreviewCfg({
      pdfUrl: `/export/tpar-pdf?fy=${data.fy}&with_evidence=1`,
      filename: `buildintel-tpar-${data.fy_label}.pdf`,
      title: `TPAR Worksheet · ${data.fy_label}`,
      description: "Verify each ABN before you Share",
      shareText: text,
      shareSubject: subject,
    });
    setPreviewOpen(true);
  };
  // eslint-disable-next-line no-unused-vars
  const _legacyDownload = async () => {
    if (!data) return;
    setDlBusy(true);
    try {
      await downloadFile(
        `/api/export/tpar-pdf?fy=${data.fy}`,
        `buildintel-tpar-${data.fy_label}.pdf`
      );
      toast.success(`TPAR ${data.fy_label} downloaded — verify each ABN before lodging`);
    } catch (err) {
      devLog.warn("tpar pdf failed", err);
      toast.error(err.message || "Download failed");
    } finally {
      setDlBusy(false);
    }
  };

  /**
   * Build a TPAR summary text — same structure across clipboard / WhatsApp /
   * Email / native share so accountants get one consistent message.
   */
  const buildSummary = useCallback(() => {
    if (!data) return "";
    const greeting = accountantName ? `Hi ${accountantName},\n\n` : "";
    const bizLine = bizName ? `${bizName}${abn ? ` · ABN ${abn}` : ""}\n` : "";
    const missing = data.missing_abn_count > 0
      ? `\n⚠ ${data.missing_abn_count} subcontractor${data.missing_abn_count === 1 ? "" : "s"} missing ABN — 47% withholding may apply.`
      : "";
    const topRows = (data.rows || []).slice(0, 5).map((r) =>
      `• ${r.supplier}${r.abn ? ` (ABN ${r.abn})` : " (ABN missing)"}: $${Number(r.gross_paid || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    ).join("\n");
    const more = (data.rows?.length || 0) > 5
      ? `\n+ ${data.rows.length - 5} more subcontractors in the PDF`
      : "";
    return (
      `${greeting}${bizLine}TPAR Worksheet · ${data.fy_label}\n\n` +
      `Subbies: ${data.subcontractor_count ?? 0}\n` +
      `Total paid: $${Number(data.total_paid || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n` +
      `GST included: $${Number(data.total_gst || 0).toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` +
      `${missing}\n\n` +
      (topRows ? `Top subbies:\n${topRows}${more}\n\n` : "") +
      `Full TPAR Worksheet PDF attached (prepared with BuildIntel AI). ` +
      `Due: 28 August. Please review each ABN before lodging.`
    );
  }, [data, accountantName, bizName, abn]);

  // eslint-disable-next-line no-unused-vars
  const shareVia = useCallback(async (_kind) => {
    if (!data) return;
    const text = buildSummary();
    const subject = bizName
      ? `${bizName} — TPAR ${data.fy_label}`
      : `TPAR ${data.fy_label} — ${data.subcontractor_count ?? 0} subcontractors`;
    setPreviewCfg({
      pdfUrl: `/export/tpar-pdf?fy=${data.fy}&with_evidence=1`,
      filename: `tpar-worksheet-${data.fy_label.toLowerCase().replace(/\s+/g,"-")}.pdf`,
      title: `TPAR Worksheet · ${data.fy_label}`,
      description: "Verify each ABN before you Share",
      shareText: text,
      shareSubject: subject,
    });
    setPreviewOpen(true);
  }, [data, buildSummary, bizName]);

  return (
    <section
      className="bi-surface p-4 space-y-3 border border-[#FF9500]/20"
      data-testid="tpar-draft"
    >
      <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-15 bg-[#FF9500] pointer-events-none" />
      <div className="flex items-center gap-2 text-[#FF9500]">
        <Users className="w-4 h-4" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-heading">TPAR One-Tap</span>
      </div>
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="font-heading text-xl font-extrabold leading-tight">
          Prepare this year&apos;s TPAR
        </h2>
        {data && (
          <span className="text-[11px] text-white/50 shrink-0" data-testid="tpar-fy">
            {data.fy_label}
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs">
        <CalendarClock className="w-3.5 h-3.5 text-white/40" />
        <BiSelect
          data-testid="tpar-fy-picker"
          value={fy || ""}
          onChange={(e) => setFy(parseInt(e.target.value, 10))}
          className="bi-input h-9 flex-1"
        >
          {options.map((y) => (
            <option key={y} value={y}>
              {`FY${String(y).slice(-2)}`}
            </option>
          ))}
        </BiSelect>
        {data?.due_date && (
          <span className="text-[10px] text-white/50 shrink-0">
            Due {fmtDate(data.due_date)}
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-2">
        <div className="bi-surface-2 py-2.5 px-3 rounded-lg" data-testid="tpar-subs">
          <div className="text-[9px] uppercase tracking-wider text-white/50">Subbies</div>
          <div className="font-heading font-extrabold text-lg tabular-nums">
            {busy && !data ? "…" : (data?.subcontractor_count ?? 0)}
          </div>
        </div>
        <div className="bi-surface-2 py-2.5 px-3 rounded-lg" data-testid="tpar-total">
          <div className="text-[9px] uppercase tracking-wider text-white/50">Paid</div>
          <div className="font-heading font-extrabold text-lg tabular-nums text-[#FF9500]">
            {busy && !data ? "…" : money(data?.total_paid)}
          </div>
        </div>
        <div className="bi-surface-2 py-2.5 px-3 rounded-lg" data-testid="tpar-gst">
          <div className="text-[9px] uppercase tracking-wider text-white/50">GST</div>
          <div className="font-heading font-extrabold text-lg tabular-nums">
            {busy && !data ? "…" : money(data?.total_gst)}
          </div>
        </div>
      </div>

      {data && data.missing_abn_count > 0 && (
        <div
          data-testid="tpar-missing-abn"
          className="text-[11px] text-[#FBBF24] inline-flex items-start gap-1.5 leading-snug"
        >
          <AlertTriangle className="w-3 h-3 mt-0.5" />
          {data.missing_abn_count} subcontractor
          {data.missing_abn_count === 1 ? "" : "s"} missing an ABN — 47% withholding may apply.
          Confirm ABNs before lodging.
        </div>
      )}

      {data && data.rows.length > 0 && (
        <div className="space-y-1" data-testid="tpar-rows">
          {data.rows.slice(0, 5).map((r) => (
            <div
              key={r.supplier}
              className="flex items-center justify-between gap-2 py-1 border-t border-white/5 first:border-t-0 text-xs"
            >
              <div className="min-w-0">
                <div className="truncate font-semibold text-white/90">{r.supplier}</div>
                <div className="text-[10px] text-white/40">
                  {r.entry_count} {r.entry_count === 1 ? "payment" : "payments"} ·{" "}
                  {r.abn ? <>ABN {r.abn}</> : <span className="text-[#FBBF24]">ABN missing</span>}
                </div>
              </div>
              <div className="tabular-nums font-heading font-extrabold text-white shrink-0">
                {money(r.gross_paid)}
              </div>
            </div>
          ))}
          {data.rows.length > 5 && (
            <div className="text-[10px] text-white/40 pt-1">
              + {data.rows.length - 5} more subcontractors in the PDF
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        data-testid="tpar-download-btn"
        onClick={download}
        disabled={dlBusy || busy || !data || (data && data.subcontractor_count === 0)}
        className="w-full h-14 rounded-xl bi-accent-btn flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_10px_30px_-15px_rgba(255,149,0,0.6)]"
      >
        {dlBusy ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" /> Building…
          </>
        ) : (
          <>
            <FileDown className="w-5 h-5" /> Prepare TPAR (PDF)
          </>
        )}
      </button>
      {data && data.subcontractor_count === 0 && (
        <div className="text-[11px] text-white/50 text-center">
          No subcontractor payments yet for FY{String(data.fy).slice(-2)}. Add entries with category
          <b> Subcontractor</b> to populate this report.
        </div>
      )}

      {data && data.subcontractor_count > 0 && (
        <div className="pt-3 border-t border-white/5" data-testid="tpar-share-row">
          {/* Pack-intelligence preflight — TPAR only cares about missing
              subcontractor ABNs. Advisory chip above the Share row. */}
          <div className="mb-2">
            <FlagsPreflightCard
              fy={data.fy}
              scope={["missing_abn_subcontractor"]}
              testid="tpar-flags-preflight"
            />
          </div>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/40 font-heading mb-2">
            <Share2 className="w-3 h-3" /> Send to {accountantName ? accountantName : "accountant"}
            {accountantEmail && (
              <span className="text-white/30 normal-case tracking-normal truncate">· {accountantEmail}</span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <button
              type="button"
              data-testid="tpar-share-whatsapp"
              onClick={() => shareVia("whatsapp")}
              className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition"
              title="Share via WhatsApp"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366]" />
              <span>WhatsApp</span>
            </button>
            <button
              type="button"
              data-testid="tpar-share-email"
              onClick={() => shareVia("email")}
              className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition"
              title="Share via Email"
            >
              <Mail className="w-4 h-4 text-[#60A5FA]" />
              <span>Email</span>
            </button>
            <button
              type="button"
              data-testid="tpar-share-native"
              onClick={() => shareVia("native")}
              className="h-11 rounded-xl bi-ghost-btn flex items-center justify-center gap-1.5 text-xs font-heading font-bold uppercase tracking-wider hover:brightness-125 active:scale-95 transition"
              title="Share via device"
            >
              <Share2 className="w-4 h-4 text-[#FF9500]" />
              <span>Share</span>
            </button>
          </div>
        </div>
      )}

      <TaxDisclaimer compact />

      <PdfPreviewSheet
        open={previewOpen}
        onOpenChange={(v) => { setPreviewOpen(v); if (!v) setPreviewCfg(null); }}
        pdfUrl={previewCfg?.pdfUrl}
        filename={previewCfg?.filename}
        title={previewCfg?.title}
        description={previewCfg?.description}
        shareText={previewCfg?.shareText}
        shareSubject={previewCfg?.shareSubject}
        testid="tpar-preview"
      />
    </section>
  );
}
